import React from 'react';

import { css, cx } from '@emotion/css';
import { Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FontSizes } from '@/assets/fonts.ts';
import { Icons } from '@/assets/icons.ts';
import { UploadButton } from '@/components/button/UploadButton.tsx';
import { Tooltip } from '@/components/floating/Tooltip.tsx';
import { useStyles } from '@/hooks/useStyles.ts';
import { Theme } from '@/theme/CreateTheme.ts';

export type PrescriptionUploadFieldProps = {
  patientInfo: {
    givenName: string;
    familyName: string;
  };
  carePlanStartDate: string;
  className?: string;
};

export const PrescriptionUploadField: React.FC<
  PrescriptionUploadFieldProps
> = ({ patientInfo, carePlanStartDate, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const formContext = useFormContext();

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.row}>
        <Typography variant="bodySmall" className={styles.text}>
          {t('diabetesForms.prescriptionDescription')}
          {' : '}
          <u>{carePlanStartDate}</u>{' '}
          <Tooltip
            className={styles.tooltip}
            content={
              <Typography variant="body" className={styles.tooltipContent}>
                {t('diabetesForms.prescriptionTooltip')}
              </Typography>
            }
            hover={{ enabled: false }}
            click={{ enabled: true }}
            placement="bottom"
          >
            <Icons.questionCircle className={styles.icon} />
          </Tooltip>{' '}
          <UploadButton
            id="upload-prescription-choice"
            onFileChange={data => {
              if (!data || !data.name) {
                formContext.setValue('file', undefined);
              } else {
                formContext.setValue('file', data);
              }

              formContext.trigger(['weight', 'birthDate']);
            }}
            hasTrash
            fileType="prescriptions"
            patientInfo={patientInfo}
            button={{
              variant: 'text',
              text: 'Cliquez ici ',
            }}
            className={styles.upload}
            buttonClassName={styles.button}
          />
        </Typography>
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({}),
  row: css({
    display: 'flex',
    justifyContent: 'center',
    flex: '1 1 0',
    alignItems: 'center',
  }),
  text: css({
    color: theme.palette.text.secondary,
  }),
  tooltip: css({
    whiteSpace: 'pre-wrap',
    display: 'inline-block',
    paddingLeft: theme.spacing(1),
  }),
  tooltipContent: css({
    whiteSpace: 'pre-wrap',
  }),
  icon: css({
    position: 'relative',
    top: 2,
  }),
  upload: css({
    display: 'inline-block',
    position: 'relative',
    top: -2,
  }),
  button: css({
    minWidth: 0,
    display: 'inline-block',
    padding: theme.spacing(0),
    margin: 0,
    fontSize: FontSizes.bodySmall,
  }),
});
